/**
 * Core-100 R4 — CORE-BC GAP-001/002/003: OptiPlace's explanation of a candidate, rendered from the typed explanation
 * (src/lib/intelligence-explanations/assignment.ts): each signal with its value, where it came from, how much data
 * stands behind it and — where its authority has one — its confidence. When an older server sent only the free-text
 * sentence, that sentence is shown as it is, labelled as having no sample basis, and still passes the judgment guard
 * in development.
 */
import { useEffect } from 'react';
import {
  describeSignalConfidence, describeSignalSample, findJudgmentLanguage, readAssignmentExplanation,
} from '@/lib/intelligence-explanations/assignment';

interface Props {
  explanation: unknown;
  fallbackText?: string | null;
}

export function AssignmentExplanationView({ explanation, fallbackText }: Props) {
  const e = readAssignmentExplanation(explanation);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const text = e ? [e.summary, ...e.signals.flatMap((s) => [s.label, s.value, s.note ?? ''])].join(' ') : fallbackText ?? '';
    const hits = findJudgmentLanguage(text);
    if (hits.length) console.warn(`[CORE-BC] judgment language in an assignment explanation: ${hits.join(', ')}`);
  }, [e, fallbackText]);

  if (!e) {
    if (!fallbackText) return null;
    return (
      <div data-testid="assignment-explanation-fallback">
        <p className="text-sm text-muted-foreground">{fallbackText}</p>
        <p className="text-xs text-muted-foreground italic">No sample sizes were sent with this explanation.</p>
      </div>
    );
  }

  return (
    <div data-testid="assignment-explanation" className="space-y-1">
      <p className="text-sm text-muted-foreground" data-testid="assignment-explanation-summary">{e.summary}</p>
      <ul className="space-y-0.5 text-xs">
        {e.signals.map((s) => {
          const sample = describeSignalSample(s);
          const conf = describeSignalConfidence(s);
          return (
            <li key={s.key} data-testid={`assignment-signal-${s.key}`}>
              <span className="text-muted-foreground">{s.label}: </span>
              <span className="font-medium">{s.value}</span>
              {sample && <span className="text-muted-foreground" data-testid={`assignment-signal-sample-${s.key}`}> · {sample}</span>}
              {conf && <span className="text-muted-foreground" data-testid={`assignment-signal-confidence-${s.key}`}> · {conf}</span>}
              <span className="text-muted-foreground"> · {s.source}</span>
              {s.note && <span className="block text-muted-foreground italic">{s.note}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
